import {
  Button,
  Heading,
  InfoBox,
  InfoListItem,
  ListContainer,
  Typography,
} from '@altify/components';
import { formatAssetAmount, useContent } from '@altify/utils';
import RewardTierIcon from 'app/_authenticated/trade/assets/RewardTierIcon';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import InfoModal from 'app/_authenticated/trade/components/InfoModal/InfoModal';
import { tradeSharedContent } from 'app/_authenticated/trade/tradeShared.content';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import { useProduct } from 'hooks/products/useProducts';
import { useMemo, useState } from 'react';
import { View } from 'react-native';
import { RecurringBuySchedule } from '../../models/RecurringBuySchedule';
import { recurringBuySharedContent } from '../../recurringBuyShared.content';
import { content } from './RecurringBuyConfirmModal.content';

const RecurringBuyConfirmModal = ({
  isOpen,
  fromProductCode,
  toProductCode,
  fromAmount,
  schedule,
  rewardTierName,
  rewardTierCode,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  fromProductCode?: string;
  toProductCode?: string;
  fromAmount: number;
  schedule: RecurringBuySchedule;
  rewardTierName: string;
  rewardTierCode: string;
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);
  const { product: toProduct } = useProduct(toProductCode);
  const localizedContent = useContent(
    content,
    recurringBuySharedContent,
    tradeSharedContent,
  );

  const rewardIconComponent = useMemo(
    () =>
      ({ className }: { className?: string }) => (
        <RewardTierIcon rewardTierCode={rewardTierCode} className="-my-4" />
      ),
    [rewardTierCode],
  );

  const listItems = [
    {
      id: 1,
      title: formatAssetAmount(fromAmount, fromProductCode ?? '', {
        showSpacer: true,
      }),
      subtitle: localizedContent.youAreUsing,
    },
    {
      id: 2,
      title: rewardTierName,
      onInfoIconPress: () => setInfoModalIsOpen(true),
      subtitle: localizedContent.rewardLevel,
      infoIconVisible: true,
      iconComponent: rewardIconComponent,
    },
  ];

  const RecurringInfoBox = () => (
    <View className="w-full">
      <InfoBox className="w-full mb-8 mt-32">
        <Typography>
          {localizedContent.infoBox(schedule, fromAmount, fromProductCode!)}
        </Typography>
      </InfoBox>
    </View>
  );

  return (
    <>
      <DialogOrBottomSheet
        blurEnabled={true}
        isOpen={isOpen}
        onClose={() => onClose?.()}
        backgroundColor="#0C1C2C"
        footerContent={
          <View className="px-4">
            <Button
              size="large"
              color="secondary"
              text={localizedContent.editDetails}
              className="mt-4 w-full"
              onPress={() => {
                onClose?.();
              }}
            />
            <Button
              size="large"
              color="primary"
              text={localizedContent.confirmBuy}
              className="my-4 w-full"
              onPress={() => {
                onSubmit();
                onClose?.();
              }}
            />
          </View>
        }>
        <View className="items-center w-full px-4">
          <div>
            <Heading level={5}>{localizedContent.confirmDetails}</Heading>
          </div>
          <View className="my-4">
            <CurrencyIcon
              size={48}
              code={toProductCode}
              cornerIconBackground={'#0C1C2C'}
              bottomRightIcon="RECURRING_BUY"
              cornerIconSize={24}
            />
          </View>
          <Typography size="large" className="text-neutral-300">
            {localizedContent.youWillBeBuying}
          </Typography>
          <Heading level={2}>{toProduct?.name}</Heading>
          <Typography
            size="large"
            weight="medium"
            className="text-neutral-300 mt-2">
            {localizedContent.recurringScheduleNow(schedule)}
          </Typography>
          <ListContainer className="mt-10">
            {listItems.map(i => (
              <InfoListItem {...i} key={i.id} />
            ))}
          </ListContainer>

          <RecurringInfoBox />
        </View>
      </DialogOrBottomSheet>
      {infoModalIsOpen && (
        <InfoModal
          isOpen={infoModalIsOpen}
          onClose={() => setInfoModalIsOpen(false)}
          infoType={'rewardLevel'}
        />
      )}
    </>
  );
};

export default RecurringBuyConfirmModal;
